import ContentIllustration from '../../../components/ui/ContentIllustration';
import { useState, useRef, useCallback, useEffect, Fragment } from 'react';
import QcmCard from '../../../components/quiz/QcmCard';
import DynamicIcon from '../../../components/ui/DynamicIcon';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import useCustomStore from '../../../store/customStore';
import { Settings, ChevronRight, Plus, Trash2, GripVertical, Pencil, Palette, Upload, Loader2, Play, Save, X, Share2, Eye, EyeOff, Archive, RotateCcw, AlertTriangle, CheckCircle, Lock, Search } from 'lucide-react';
import api from '../../../services/api';
import SujetEditModal from '../../../components/custom/SujetEditModal';
import SuggestedLabel from '../../../components/custom/SuggestedLabel';
import CategoryCopyCodeModal from '../../../components/custom/CategoryCopyCodeModal';
import useEscapeClose from '../../../hooks/useEscapeClose';
import RequestMoreQuestions, { useContentRequests } from './RequestMoreQuestions';

const PRESET_COLORS = ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#6366F1', '#14B8A6', '#F97316', '#64748B'];

// Sous-groupes par domaine de suivi (Comptabilité, Droit privé…) dans les grosses catégories.
// Affichage seul : les sujets restent dans leur catégorie. Activé à partir de 8 sujets et
// quand les domaines regroupent en moyenne au moins 2 sujets (sinon des groupes d'une carte).
// Un groupe d'affichage posé sur le sujet (migration 211, ex. maths : un seul domaine) passe avant le domaine.
const SEUIL_GROUPES = 8;
function groupesParDomaine(liste) {
    if (liste.length < SEUIL_GROUPES || liste.some(ss => !ss.groupe && !ss.domaine_id)) return null;
    const map = new Map();
    for (const ss of liste) {
        const id = ss.groupe ? `g:${ss.groupe}` : ss.domaine_id;
        if (!map.has(id)) map.set(id, { id, label: ss.groupe || ss.domaine_label || 'Autres', items: [] });
        map.get(id).items.push(ss);
    }
    const groupes = [...map.values()];
    return groupes.length >= 2 && liste.length / groupes.length >= 2 ? groupes : null;
}
// « Option Comptabilité Inspecteur — Analyse financière » → « Analyse financière » sous l'en-tête
// « Comptabilité » ; le libellé complet reste celui du sujet partout ailleurs.
function libelleCourt(label) {
    const i = String(label || '').indexOf(' — ');
    return i > 0 ? label.slice(i + 3) : null;
}

export default function TabQuiz({
    categories, has_access, user, openCategories, toggleCategory,
    onStart, onDirectLaunch, onLocked, isCompte, scope,
    ignoredIds = new Set(), onToggleIgnore,
    isMaThematique: isMaThematiqueProp, isReadOnly = false, thematiqueId, onRefresh,
    filterFreeOnly = false, lastSousSujetId = null,
    onResume,
    showObsolete = false, onToggleShowObsolete,
    renderGestionSujets = null, // callback ReactNode — affiché en haut, uniquement pour Ma Thématique
    onOpenGestion = null, // callback — ouvre la modale Gestion des sujets (hub uniquement)
    isTrial = false, // essai gratuit (migration 181) : catégories réservées à l'abonnement
    accentColor = null, // couleur primaire de la thématique → teinte le hero « Quiz par sujet »
}) {
    const isMaThematique = isMaThematiqueProp && !isReadOnly; // read-only shared thématiques disable editing
    const [settingsCatId, setSettingsCatId] = useState(null);
    const [search, setSearch] = useState('');
    const [openDomaines, setOpenDomaines] = useState({}); // { 'catId:domaineId': true }
    // Mobile, compte gratuit : sujets verrouillés regroupés en une ligne dépliable par liste (audit mobile 18/10).
    const [openLocked, setOpenLocked] = useState({});
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 640px)').matches);
    useEffect(() => {
        const mq = window.matchMedia?.('(max-width: 640px)');
        if (!mq) return undefined;
        const on = () => setIsMobile(mq.matches);
        mq.addEventListener?.('change', on);
        return () => mq.removeEventListener?.('change', on);
    }, []);

    // SujetEditModal (unified sujet edit interface)
    const [editSujet, setEditSujet] = useState(null); // { id, custom_ss_id, label, nb_questions }

    const isAdmin = user?.role === 'admin';
    // « Demander plus de questions » : thématiques éditoriales, espace connecté (le serveur écarte perso et partenaires).
    const contentRequests = useContentRequests(scope, !!user && isCompte && !isMaThematiqueProp && !isReadOnly);

    // Ma Thématique category management state
    const [newCatName, setNewCatName] = useState('');
    const [editingCatId, setEditingCatId] = useState(null);
    const [editingLabel, setEditingLabel] = useState('');
    const [createCatNotice, setCreateCatNotice] = useState(null); // toast auto-dismiss

    // Category copy-code share state (owner-side only — l'import par code se fait
    // depuis la modale "Gestion des sujets" → MaThematiqueUpload).
    const [copyCodeCat, setCopyCodeCat] = useState(null); // { id, label } — opens CategoryCopyCodeModal

    // --- Obsolete / restore ------------------------------------------------
    const [confirmObsolete, setConfirmObsolete] = useState(null); // { customSsId, label }
    const [obsoleteBlockers, setObsoleteBlockers] = useState(null); // { custom_ss_id, label, blockers }
    const [obsoleteBusy, setObsoleteBusy] = useState(null); // custom_ss_id being processed
    // ESC closes blocker / obsolete-confirm modals (visual-order priority).
    useEscapeClose(!!obsoleteBlockers || !!confirmObsolete, () => {
        if (obsoleteBlockers) setObsoleteBlockers(null);
        else if (confirmObsolete) setConfirmObsolete(null);
    });
    const handleObsolete = async (customSsId, label) => {
        setObsoleteBusy(customSsId);
        try {
            await api.post('/custom/sujet-obsolete.php', { custom_ss_id: customSsId });
            if (onRefresh) onRefresh();
        } catch (e) {
            // jsonError wraps the extra payload under "errors", not "data".
            const blockers = e.response?.data?.errors?.blockers;
            if (e.response?.status === 409 && blockers) {
                setObsoleteBlockers({ customSsId, label, blockers });
            } else {
                console.warn('Erreur:', e.response?.data?.message || e.message);
            }
        }
        setObsoleteBusy(null);
    };
    const handleRestore = async (customSsId) => {
        setObsoleteBusy(customSsId);
        try {
            await api.post('/custom/sujet-restore.php', { custom_ss_id: customSsId });
            if (onRefresh) onRefresh();
        } catch (e) {
            console.warn('Erreur:', e.response?.data?.message || e.message);
        }
        setObsoleteBusy(null);
    };
    const { createCategory, renameCategory, deleteCategory, moveSousSujet, reorderSousSujets, reorderCategories } = useCustomStore();
    const [customizingCatId, setCustomizingCatId] = useState(null);
    const [uploadingCatImage, setUploadingCatImage] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null); // { catId, label }

    // Drag & drop state
    const dragItemRef = useRef(null);      // { customSsId, sousSujetId }
    const dragCatRef = useRef(null);       // { catId } when dragging a category
    const [dragOverCatId, setDragOverCatId] = useState(null);
    const [dragOverCatReorder, setDragOverCatReorder] = useState(null); // catId being hovered for cat reorder
    const [isDragging, setIsDragging] = useState(false); // true pendant un drag actif (réactif, contrairement aux refs)

    // `sousSujets` doit refléter la liste actuellement visible (post-filtres
    // obsolète/gratuit) pour que la barre soit cohérente avec ce qui s'affiche.
    const getCatProgression = (sousSujets) => {
        if (!user) return null;
        const active = sousSujets.filter(ss => !ignoredIds.has(ss.id));
        const totalQ = active.reduce((s, ss) => s + (ss.nb_questions || 0), 0);
        const answered = active.reduce((s, ss) => s + (ss.answered_count || 0), 0);
        const mastered = active.reduce((s, ss) => s + (ss.mastered_count || 0), 0);
        return { totalQ, answered, mastered };
    };

    const canManage = isCompte && user && onToggleIgnore;

    // --- Ma Thématique category handlers ---
    const handleCreateCat = async () => {
        const trimmed = newCatName.trim();
        if (!trimmed) return;
        try {
            const result = await createCategory(trimmed, thematiqueId);
            setNewCatName('');
            if (onRefresh) onRefresh();

            // Toast de confirmation, auto-dismiss
            setCreateCatNotice(`Catégorie « ${result?.label || trimmed} » créée`);
            setTimeout(() => setCreateCatNotice(null), 3000);

            // Scroll vers la nouvelle catégorie une fois qu'elle a été ré-injectée
            // dans le DOM par fetchList. Poll DOM jusqu'à 1s (createCategory ne
            // attend pas fetchList côté store, d'où le polling défensif).
            const newId = result?.id;
            if (newId) {
                let tries = 0;
                const tryScroll = () => {
                    const el = document.getElementById(`cat-ucat_${newId}`);
                    if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        return;
                    }
                    if (tries++ < 20) setTimeout(tryScroll, 50);
                };
                setTimeout(tryScroll, 50);
            }
        } catch (e) { console.warn('Erreur:', e); }
    };

    const handleRenameCat = async (catId) => {
        if (!editingLabel.trim()) { setEditingCatId(null); return; }
        await renameCategory(catId, editingLabel.trim());
        setEditingCatId(null);
        if (onRefresh) onRefresh();
    };

    const handleDeleteCat = (catId, label) => {
        setConfirmDelete({ catId, label });
    };

    const executeDeleteCat = async () => {
        if (!confirmDelete) return;
        await deleteCategory(confirmDelete.catId);
        setConfirmDelete(null);
        if (onRefresh) onRefresh();
    };

    const handleCatColor = async (catId, color) => {
        const realId = String(catId).replace('ucat_', '');
        await api.put('/custom/categories.php', { id: Number(realId), couleur_theme: color });
        if (onRefresh) onRefresh();
    };

    const handleCatImageUpload = async (e, catId) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const realId = String(catId).replace('ucat_', '');
        setUploadingCatImage(true);
        try {
            const formData = new FormData();
            formData.append('image', file);
            formData.append('category_id', realId);
            await api.post('/custom/categories.php?action=upload_image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (onRefresh) onRefresh();
        } catch (e) { console.warn('Erreur:', e); }
        setUploadingCatImage(false);
        e.target.value = '';
    };

    // --- Drag & drop handlers ---
    const handleDragStart = useCallback((e, sousSujet, cat) => {
        dragItemRef.current = { customSsId: Number(sousSujet.custom_ss_id), ssId: sousSujet.id, fromCatId: cat.id };
        dragCatRef.current = null;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(sousSujet.id));
        // Set drag image au wrapper entier pour un feedback visuel propre
        const wrapper = e.currentTarget.closest('.mt-draggable-card');
        if (wrapper) {
            try { e.dataTransfer.setDragImage(wrapper, 20, 20); } catch {}
        }
        // Décale le state update au frame suivant : sinon React re-render synchronement
        // pendant le traitement du dragstart, mute le DOM (drop hints sur cats vides)
        // et le navigateur cancel le drag (dragend immédiat avec dropEffect=none).
        requestAnimationFrame(() => setIsDragging(true));
    }, []);

    const handleCatDragStart = useCallback((e, catId) => {
        dragCatRef.current = { catId };
        dragItemRef.current = null;
        setIsDragging(true);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('cat-reorder', String(catId));
        e.stopPropagation();
    }, []);

    const handleDragOver = useCallback((e, catId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragCatRef.current) {
            // Category reorder in progress — highlight only the header
            setDragOverCatReorder(catId);
            setDragOverCatId(null);
        } else {
            setDragOverCatId(catId);
        }
    }, []);

    const handleDragLeave = useCallback((e, catId) => {
        // Only clear if leaving the container (not entering a child)
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragOverCatId(null);
            setDragOverCatReorder(null);
        }
    }, []);

    const handleDrop = useCallback(async (e, targetCatId) => {
        e.preventDefault();
        setDragOverCatId(null);
        setDragOverCatReorder(null);

        // Category reorder takes precedence
        if (dragCatRef.current) {
            const { catId: fromCatId } = dragCatRef.current;
            dragCatRef.current = null;
            if (fromCatId === targetCatId) return;
            // Only reorder among real user categories (skip ucat_0 "Non classé")
            const userCats = categories.filter(c => String(c.id).startsWith('ucat_') && c.id !== 'ucat_0');
            const fromIdx = userCats.findIndex(c => c.id === fromCatId);
            const toIdx = userCats.findIndex(c => c.id === targetCatId);
            if (fromIdx === -1 || toIdx === -1) return;
            const reordered = [...userCats];
            const [moved] = reordered.splice(fromIdx, 1);
            reordered.splice(toIdx, 0, moved);
            const orderedItems = reordered.map((c, i) => ({ id: Number(String(c.id).replace('ucat_', '')), ordre: i }));
            await reorderCategories(thematiqueId, orderedItems);
            if (onRefresh) onRefresh();
            return;
        }

        if (!dragItemRef.current) return;

        const { customSsId, ssId, fromCatId } = dragItemRef.current;
        dragItemRef.current = null;

        if (fromCatId === targetCatId) {
            // Same category — reorder based on drop position
            const dropTarget = e.target.closest('[data-ss-id]');
            if (!dropTarget) return;
            const targetSsId = Number(dropTarget.dataset.ssId);
            if (targetSsId === ssId) return; // dropped on itself

            // Find the category and its sous-sujets
            const cat = categories.find(c => c.id === targetCatId);
            if (!cat) return;
            const ssList = [...cat.sous_sujets];
            const fromIdx = ssList.findIndex(s => s.id === ssId);
            const toIdx = ssList.findIndex(s => s.id === targetSsId);
            if (fromIdx === -1 || toIdx === -1) return;

            // Reorder
            const [moved] = ssList.splice(fromIdx, 1);
            ssList.splice(toIdx, 0, moved);

            // Save new order
            const orderedItems = ssList.map((s, i) => ({ id: s.custom_ss_id, ordre: i }));
            await reorderSousSujets(thematiqueId, orderedItems);
            if (onRefresh) onRefresh();
            return;
        }

        // Different category — move
        const realCatId = targetCatId === 'ucat_0' ? null : Number(String(targetCatId).replace('ucat_', ''));
        await moveSousSujet(customSsId, realCatId);
        if (onRefresh) onRefresh();
    }, [moveSousSujet, reorderSousSujets, onRefresh, categories, thematiqueId]);

    const handleDragEnd = useCallback(() => {
        dragItemRef.current = null;
        dragCatRef.current = null;
        setDragOverCatId(null);
        setDragOverCatReorder(null);
        setIsDragging(false);
    }, []);

    // Auto-scroll de la fenêtre pendant un drag : si le curseur s'approche
    // du haut ou du bas du viewport, on scrolle pour révéler les catégories
    // hors-écran (utile pour déposer un sujet dans une catégorie non visible).
    useEffect(() => {
        if (!isDragging) return;

        const EDGE = 90;        // px : taille de la zone de déclenchement près des bords
        const MAX_SPEED = 22;   // px/frame : vitesse de scroll maximale (au bord exact)
        let rafId = null;
        let speed = 0;

        const tick = () => {
            if (speed === 0) { rafId = null; return; }
            window.scrollBy(0, speed);
            rafId = requestAnimationFrame(tick);
        };

        const onDragOver = (e) => {
            const y = e.clientY;
            const h = window.innerHeight;
            if (y < EDGE) {
                const ratio = (EDGE - y) / EDGE; // 0 → 1 plus on s'approche du bord
                speed = -Math.ceil(MAX_SPEED * ratio);
            } else if (y > h - EDGE) {
                const ratio = (y - (h - EDGE)) / EDGE;
                speed = Math.ceil(MAX_SPEED * ratio);
            } else {
                speed = 0;
            }
            if (speed !== 0 && rafId === null) rafId = requestAnimationFrame(tick);
        };

        window.addEventListener('dragover', onDragOver);
        return () => {
            window.removeEventListener('dragover', onDragOver);
            if (rafId !== null) cancelAnimationFrame(rafId);
        };
    }, [isDragging]);

    // Exclure les sous-sujets OBSOLETE des totaux (cohérent avec l'affichage et le header API).
    const activeSs = (c) => c.sous_sujets.filter(ss => ss.custom_status !== 'OBSOLETE');
    const totalSs = categories.reduce((s, c) => s + activeSs(c).length, 0); // total réel (pour le CTA "vide")

    // Recherche de sujet (filtre live + auto-déplie les catégories).
    const searchLc = search.trim().toLowerCase();

    // Liste réellement affichée par catégorie (obsolète + gratuit + recherche),
    // alignée 1:1 avec categories[] → sert au rendu ET aux KPI synchronisés.
    const displayedLists = categories.map(cat => {
        const vis = cat.sous_sujets.filter(ss => showObsolete || ss.custom_status !== 'OBSOLETE');
        const free = filterFreeOnly ? vis.filter(ss => ss.is_demo) : vis;
        if (!searchLc) return free;
        const catMatch = (cat.label || '').toLowerCase().includes(searchLc);
        return catMatch ? free : free.filter(ss => (ss.label || '').toLowerCase().includes(searchLc));
    });
    const anySearchMatch = !searchLc || displayedLists.some(list => list.length > 0);
    const dispQ = displayedLists.reduce((s, list) => s + list.reduce((a, ss) => a + (ss.nb_questions || 0), 0), 0);
    const dispQrc = displayedLists.reduce((s, list) => s + list.reduce((a, ss) => a + (ss.qrc_count || 0), 0), 0);
    const dispSs = displayedLists.reduce((s, list) => s + list.length, 0);
    const dispCats = displayedLists.filter(list => list.length > 0).length;
    const dispEnCours = displayedLists.reduce((s, list) => s + list.filter(ss => ss.has_session).length, 0);

    // Barre "Nouvelle catégorie" — affichée juste avant « Non classé » (ucat_0) dans la map,
    // fallback en fin de liste si Non classé n'existe pas.
    const createCatBar = isMaThematique && has_access && !isTrial ? (
        <>
            <div className="mt-cat-bar">
                <input
                    type="text"
                    className="mt-cat-bar__input"
                    placeholder="Nouvelle catégorie..."
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateCat()}
                    maxLength={100}
                />
                <button className="btn btn-sm btn-primary" onClick={handleCreateCat} disabled={!newCatName.trim()}>
                    <Plus size={14} /> Créer
                </button>
            </div>
            {createCatNotice && (
                <div className="sem-notice sem-notice--info" style={{ marginTop: 6, animation: 'fade-up 0.25s ease-out' }}>
                    <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    {createCatNotice}
                </div>
            )}
        </>
    ) : null;
    const hasNonClasse = categories.some(c => c.id === 'ucat_0');

    return (
        <div className="sl-examens">
            {/* Section "Gestion des sujets" (Ma Thématique uniquement) — Upload + liste générations */}
            {isMaThematique && renderGestionSujets && (
                <div className="sl-gestion-sujets" style={{ marginBottom: 24 }}>
                    {renderGestionSujets()}
                </div>
            )}

            <div className="sl-quiz-hero" style={accentColor ? { '--quiz-accent': accentColor } : undefined}>
                <div className="sl-quiz-hero__top">
                    <div className="sl-quiz-hero__title-wrap">
                        <span className="sl-quiz-hero__icon"><ContentIllustration name="check-square" size={48} /></span>
                        <div>
                            <h3 className="sl-quiz-hero__title">Quiz par sujet</h3>
                            <p className="sl-quiz-hero__sub">Choisis un sujet, lance en classique ou en apprentissage.</p>
                        </div>
                    </div>
                    <div className="sl-quiz-hero__search">
                        <Search size={15} />
                        <input
                            type="text"
                            placeholder="Rechercher un sujet…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label="Rechercher un sujet"
                        />
                        {search && (
                            <button type="button" onClick={() => setSearch('')} aria-label="Effacer la recherche"><X size={14} /></button>
                        )}
                    </div>
                </div>
                <div className="sl-quiz-hero__kpis">
                    <div className="sl-quiz-hero__kpi"><div className="sl-quiz-hero__kpi-num">{dispQ.toLocaleString('fr-FR')}</div><div className="sl-quiz-hero__kpi-lbl">QUESTIONS</div></div>
                    {dispQrc > 0 && (
                        <div className="sl-quiz-hero__kpi"><div className="sl-quiz-hero__kpi-num">{dispQrc}</div><div className="sl-quiz-hero__kpi-lbl">QRC</div></div>
                    )}
                    <div className="sl-quiz-hero__kpi"><div className="sl-quiz-hero__kpi-num">{dispSs}</div><div className="sl-quiz-hero__kpi-lbl">SUJETS</div></div>
                    <div className="sl-quiz-hero__kpi"><div className="sl-quiz-hero__kpi-num">{dispCats}</div><div className="sl-quiz-hero__kpi-lbl">CATÉGORIES</div></div>
                    <div className="sl-quiz-hero__kpi"><div className="sl-quiz-hero__kpi-num">{dispEnCours}</div><div className="sl-quiz-hero__kpi-lbl">EN COURS</div></div>
                </div>
            </div>

            {/* CTA central — thématique vide (aucun sujet) — ouvre la modale Gestion des sujets */}
            {isMaThematique && has_access && !isTrial && totalSs === 0 && onOpenGestion && (
                <div className="sl-empty-cta">
                    <div className="sl-empty-cta__icon"><ContentIllustration name="settings" size={48} /></div>
                    <h3 className="sl-empty-cta__title">Votre thématique est vide</h3>
                    <p className="sl-empty-cta__desc">
                        Uploadez votre premier document, l'IA générera des QCM personnalisés à partir de son contenu.
                    </p>
                    <button className="btn btn-primary" onClick={() => onOpenGestion()}>
                        <Settings size={16} /> Ajouter mes premiers sujets
                    </button>
                    <p className="sl-empty-cta__hint">
                        Vous retrouverez cette configuration à tout moment via l'icône <Settings size={12} style={{ verticalAlign: 'middle' }} /> en haut à droite de la thématique.
                    </p>
                </div>
            )}

            {/* Reprise rapide — quiz sessions in progress (masquée pendant une recherche) */}
            {!searchLc && user && (() => {
                const activeSessions = categories
                    .flatMap(c => c.sous_sujets.map(ss => ({ ...ss, matiere_label: c.label, cat_id: c.id })))
                    .filter(ss => ss.has_session)
                    // La séance touchée en dernier d'abord : c'est presque toujours celle qu'on veut reprendre.
                    .sort((a, b) => (b.session_updated_at || b.session_created_at || '').localeCompare(a.session_updated_at || a.session_created_at || ''));
                if (activeSessions.length === 0) return null;
                const dateFr = (ts) => (ts ? new Date(ts.replace(' ', 'T')) : null);

                // Annuler une séance démarrée sans aucune réponse (29/10/2026) : rien à perdre, le serveur le
                // revérifie au moment de supprimer (only_if_empty) ; s'il refuse, la liste est relue avec le vrai compteur.
                const cancelEmptySession = async (sessionId) => {
                    try {
                        await api.delete('/qcm/session.php', { data: { session_id: sessionId, only_if_empty: true } });
                    } catch (e) { console.warn('Annulation de séance refusée :', e); }
                    if (onRefresh) onRefresh();
                };

                // Ouvre la catégorie si fermée puis scroll vers la card du sujet
                const navigateToSujet = (catId, ssId) => {
                    if (!openCategories[catId]) toggleCategory(catId);
                    // Laisse React rerender la catégorie ouverte avant de scroller
                    setTimeout(() => {
                        const el = document.querySelector(`[data-ss-id="${ssId}"]`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 120);
                };

                return (
                    <div id="reprise-rapide" className="sl-continue-wrap">
                        <h3 className="sl-continue-heading">Reprise rapide</h3>
                        {activeSessions.map(ss => {
                            // Compteur de la SESSION en cours (et non lifetime)
                            const sessAnswered = ss.session_answered ?? 0;
                            const sessTotal    = ss.session_total ?? 0;
                            const debut = dateFr(ss.session_created_at);
                            const sessStarted = debut
                                ? debut.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                : null;
                            // Dernière réponse : seulement si la séance a avancé après son ouverture ; heure seule le même jour.
                            const derniere = sessAnswered > 0 ? dateFr(ss.session_updated_at) : null;
                            const sessLast = derniere && debut && derniere - debut > 60000
                                ? (derniere.toDateString() === debut.toDateString()
                                    ? `à ${derniere.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
                                    : `le ${derniere.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`)
                                : null;
                            return (
                                <div
                                    key={ss.id}
                                    className="sl-error-pending-card sl-continue-card"
                                    style={{ '--pick-color': '#6366f1', cursor: has_access ? 'pointer' : 'default' }}
                                    onClick={has_access ? () => navigateToSujet(ss.cat_id, ss.id) : undefined}
                                    title={has_access ? 'Voir ce sujet dans la liste' : 'Renouvelez votre abonnement pour reprendre cette session'}
                                >
                                    <span className="sl-error-pending-indicator" />
                                    <div className="sl-error-pending-info">
                                        <span className="sl-continue-cat">{ss.matiere_label}</span>
                                        <strong>{ss.label}</strong>
                                        <span>
                                            {ss.session_mode === 'apprentissage' ? 'Apprentissage' : 'Classique'}
                                            {sessTotal > 0 ? ` · ${sessAnswered}/${sessTotal}` : ''}
                                            {sessStarted ? ` · démarrée le ${sessStarted}` : ''}
                                        </span>
                                        {sessLast && <span>Dernière réponse {sessLast}</span>}
                                    </div>
                                    <button
                                        className="btn btn-primary btn-sm sl-continue-resume"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            // Verrouillé : ouvre les offres pour pouvoir reprendre la séance.
                                            if (has_access) onResume(ss.id, ss.session_mode, ss.nb_questions);
                                            else onLocked?.();
                                        }}
                                    >
                                        {has_access ? <><Play size={16} /> Reprendre</> : <><Lock size={12} style={{ marginRight: 6 }} /> Verrouillé</>}
                                    </button>
                                    {sessAnswered === 0 && ss.session_id && (
                                        <button
                                            className="btn btn-xs btn-ghost sl-error-pending-cancel"
                                            title="Annuler cette séance (aucune réponse)"
                                            onClick={(e) => { e.stopPropagation(); cancelEmptySession(ss.session_id); }}
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })()}

            {/* Les réponses rédigées ont leur propre onglet « Rédaction » depuis le 29/09/2026
                (TabRedaction, carte du dock « Répondre à des questions courtes »). */}

            {categories.map((cat, ci) => {
                // Liste affichée (obsolète + gratuit + recherche) — même calcul que displayedLists.
                const filteredSousSujets = displayedLists[ci];
                // Masque la catégorie sans résultat seulement quand un filtre est actif
                // (sans filtre, une catégorie vide reste affichée avec son message).
                if ((searchLc || filterFreeOnly) && filteredSousSujets.length === 0) return null;

                const isOpen = searchLc ? true : !!openCategories[cat.id];
                const prog = getCatProgression(filteredSousSujets);
                const ignoredCount = filteredSousSujets.filter(ss => ignoredIds.has(ss.id)).length;
                const activeCount = filteredSousSujets.length - ignoredCount;
                // Sujets « Nouveau » parmi ceux affichés : le compteur suit les filtres et la recherche.
                const nouveauxCount = filteredSousSujets.filter(ss => ss.nouveau).length;
                const isSettingsOpen = settingsCatId === cat.id;
                const isUserCat = isMaThematique && String(cat.id).startsWith('ucat_');
                const isDragOver = dragOverCatId === cat.id;

                return (
                    <Fragment key={cat.id}>
                        {cat.id === 'ucat_0' && createCatBar}
                    <div
                        id={`cat-${cat.id}`}
                        className={`sl-category${cat.image ? ' has-bg-image' : ''}${isDragOver ? ' sl-category--drag-over' : ''}`}
                        style={{
                            ...(cat.couleur_theme ? { '--cat-accent': cat.couleur_theme } : {}),
                            ...(cat.image ? { '--cat-bg-image': `url(${cat.image})` } : {}),
                        }}
                        onDragOver={isMaThematique ? (e) => handleDragOver(e, cat.id) : undefined}
                        onDragLeave={isMaThematique ? (e) => handleDragLeave(e, cat.id) : undefined}
                        onDrop={isMaThematique ? (e) => handleDrop(e, cat.id) : undefined}
                    >
                        <button
                            className={`sl-category-header${dragOverCatReorder === cat.id ? ' sl-category-header--drop-target' : ''}`}
                            onClick={() => { if (editingCatId !== cat.id) toggleCategory(cat.id); }}
                        >
                            <div className="sl-category-left">
                                {isUserCat && cat.id !== 'ucat_0' && (
                                    <span
                                        className="mt-cat-drag-handle"
                                        draggable
                                        onDragStart={(e) => handleCatDragStart(e, cat.id)}
                                        onDragEnd={handleDragEnd}
                                        onClick={(e) => e.stopPropagation()}
                                        title="Glisser pour réordonner"
                                        aria-label="Réordonner la catégorie"
                                    >
                                        <GripVertical size={14} />
                                    </span>
                                )}
                                <span className={`sl-category-chevron ${isOpen ? 'open' : ''}`}><ChevronRight size={14} strokeWidth={2} /></span>
                                {cat.image ? (
                                    <span className="sl-category-icon sl-category-icon--img" style={cat.couleur_theme ? { '--cat-accent': cat.couleur_theme } : undefined}>
                                        <img src={cat.image} alt="" />
                                    </span>
                                ) : cat.icon ? (
                                    <span className="sl-category-icon" style={cat.couleur_theme ? { color: cat.couleur_theme } : undefined}>
                                        <DynamicIcon name={cat.icon} size={18} />
                                    </span>
                                ) : null}

                                {/* Editable label for user categories */}
                                {isUserCat && editingCatId === cat.id ? (
                                    <span className="mt-cat-edit-wrap" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            className="mt-cat-edit-input"
                                            value={editingLabel}
                                            onChange={(e) => setEditingLabel(e.target.value)}
                                            onKeyDown={(e) => {
                                                e.stopPropagation();
                                                if (e.key === 'Enter') { e.preventDefault(); handleRenameCat(cat.id.replace('ucat_', '')); }
                                                if (e.key === 'Escape') { e.preventDefault(); setEditingCatId(null); }
                                            }}
                                            onKeyUp={(e) => e.stopPropagation()}
                                            onKeyPress={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                            autoFocus
                                        />
                                        <button
                                            type="button"
                                            className="mt-cat-edit-btn mt-cat-edit-btn--save"
                                            onClick={(e) => { e.stopPropagation(); handleRenameCat(cat.id.replace('ucat_', '')); }}
                                            title="Valider"
                                            aria-label="Valider le renommage"
                                        >
                                            <Save size={13} />
                                        </button>
                                        <button
                                            type="button"
                                            className="mt-cat-edit-btn mt-cat-edit-btn--cancel"
                                            onClick={(e) => { e.stopPropagation(); setEditingCatId(null); }}
                                            title="Annuler"
                                            aria-label="Annuler"
                                        >
                                            <X size={13} />
                                        </button>
                                    </span>
                                ) : (
                                    <h3 className="sl-category-title">{cat.label}</h3>
                                )}
                            </div>
                            <div className="sl-category-right">
                                {prog && prog.totalQ > 0 && (
                                    <div className="sl-category-progression"
                                         title={`${prog.mastered} maîtrisée${prog.mastered > 1 ? 's' : ''} · ${prog.answered} vue${prog.answered > 1 ? 's' : ''} / ${prog.totalQ}`}>
                                        <div className="sl-cat-progress-bar">
                                            <div className="sl-cat-progress-seg sl-cat-progress-seg--mastered" style={{ width: `${(prog.mastered / prog.totalQ) * 100}%` }} />
                                            <div className="sl-cat-progress-seg sl-cat-progress-seg--seen" style={{ width: `${(Math.max(0, prog.answered - prog.mastered) / prog.totalQ) * 100}%` }} />
                                        </div>
                                        <span className="sl-cat-progress-text">{prog.answered}/{prog.totalQ}</span>
                                    </div>
                                )}
                                <span className="sl-category-count">
                                    {activeCount} sujet{activeCount !== 1 ? 's' : ''}
                                    {nouveauxCount > 0 && (
                                        <span className="sl-category-new">{nouveauxCount} nouveau{nouveauxCount > 1 ? 'x' : ''}</span>
                                    )}
                                    {ignoredCount > 0 && (
                                        <span className="sl-ignored-badge">{ignoredCount} ignoré{ignoredCount > 1 ? 's' : ''}</span>
                                    )}
                                </span>

                                {/* "Afficher les obsolètes" toggle on Non classé (aligned with other categories' action icons) */}
                                {isMaThematique && cat.id === 'ucat_0' && onToggleShowObsolete && (
                                    <span className="mt-cat-actions" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            className={`mt-obsolete-toggle ${showObsolete ? 'mt-obsolete-toggle--active' : ''}`}
                                            onClick={onToggleShowObsolete}
                                            title={showObsolete ? 'Masquer les sujets obsolètes' : 'Afficher les sujets obsolètes'}
                                        >
                                            {showObsolete ? <EyeOff size={13} /> : <Eye size={13} />}
                                            <span>Obsolètes</span>
                                        </button>
                                    </span>
                                )}

                                {/* Category actions for user categories (not "Non classé") */}
                                {isUserCat && cat.id === 'ucat_0' && onOpenGestion && has_access && !isTrial && (
                                    <span className="mt-cat-actions" onClick={(e) => e.stopPropagation()}>
                                        <button className="mt-cat-action-btn" onClick={() => onOpenGestion(0)} title="Gestion des sujets (ajouter un sujet sans catégorie)">
                                            <Settings size={13} />
                                        </button>
                                    </span>
                                )}
                                {isUserCat && cat.id !== 'ucat_0' && !isTrial && (
                                    <span className="mt-cat-actions" onClick={(e) => e.stopPropagation()}>
                                        {onOpenGestion && has_access && (
                                            <button
                                                className="mt-cat-action-btn"
                                                onClick={() => onOpenGestion(Number(String(cat.id).replace('ucat_', '')))}
                                                title="Gestion des sujets (ajouter un sujet dans cette catégorie)"
                                            >
                                                <Settings size={13} />
                                            </button>
                                        )}
                                        <button
                                            className="mt-cat-action-btn"
                                            onClick={() => setCustomizingCatId(customizingCatId === cat.id ? null : cat.id)}
                                            title="Personnaliser"
                                        >
                                            <Palette size={13} />
                                        </button>
                                        <button
                                            className="mt-cat-action-btn"
                                            onClick={() => { setEditingCatId(cat.id); setEditingLabel(cat.label); }}
                                            title="Renommer"
                                        >
                                            <Pencil size={13} />
                                        </button>
                                        <button
                                            className="mt-cat-action-btn"
                                            onClick={() => setCopyCodeCat({ id: Number(String(cat.id).replace('ucat_', '')), label: cat.label })}
                                            title="Code de copie (partager le contenu)"
                                        >
                                            <Share2 size={13} />
                                        </button>
                                        <button
                                            className="mt-cat-action-btn mt-cat-action-btn--danger"
                                            onClick={() => handleDeleteCat(cat.id.replace('ucat_', ''), cat.label)}
                                            title="Supprimer"
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </span>
                                )}

                                {canManage && !isMaThematique && (
                                    <button
                                        className={`sl-cat-settings-btn ${isSettingsOpen ? 'active' : ''}`}
                                        onClick={e => { e.stopPropagation(); setSettingsCatId(isSettingsOpen ? null : cat.id); }}
                                        title="Gérer les sujets à préparer"
                                    >
                                        <Settings size={14} />
                                    </button>
                                )}
                            </div>
                        </button>

                        {customizingCatId === cat.id && isUserCat && (
                            <div className="mt-cat-customize" onClick={(e) => e.stopPropagation()}>
                                <div className="mt-cat-customize__section">
                                    <label>Couleur</label>
                                    <div className="mt-cat-customize__colors">
                                        {PRESET_COLORS.map(c => (
                                            <button key={c}
                                                className={`mt-cat-customize__swatch ${cat.couleur_theme === c ? 'mt-cat-customize__swatch--active' : ''}`}
                                                style={{ background: c }}
                                                onClick={() => handleCatColor(cat.id, c)}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <div className="mt-cat-customize__section">
                                    <label>Image</label>
                                    <div className="mt-cat-customize__image">
                                        {cat.image && <img src={cat.image} alt="" className="mt-cat-customize__preview" />}
                                        <label className="btn btn-xs btn-secondary">
                                            {uploadingCatImage ? <Loader2 size={12} className="spin" /> : <Upload size={12} />}
                                            {cat.image ? 'Changer' : 'Uploader'}
                                            <input type="file" accept="image/*" onChange={(e) => handleCatImageUpload(e, cat.id)} hidden />
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        {isSettingsOpen && !isMaThematique && (
                            <div className="sl-cat-settings-panel">
                                <p className="sl-cat-settings-title">Sujets à préparer pour cette catégorie</p>
                                <div className="sl-cat-settings-list">
                                    {cat.sous_sujets.map(ss => (
                                        <label key={ss.id} className={`sl-cat-settings-item ${ignoredIds.has(ss.id) ? 'ignored' : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={!ignoredIds.has(ss.id)}
                                                onChange={() => onToggleIgnore(ss.id)}
                                            />
                                            <span>{ss.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {isOpen && (
                            <div className="sl-category-content">
                                {(() => {
                                    const renderCard = (ss, labelCourt = null) => {
                                        const ssIsObsolete = ss.custom_status === 'OBSOLETE';
                                        return (
                                        <div
                                            key={ss.id}
                                            data-ss-id={ss.id}
                                            data-custom-ss-id={ss.custom_ss_id}
                                            onDragOver={isMaThematique ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } : undefined}
                                            onDrop={isMaThematique ? (e) => handleDrop(e, cat.id) : undefined}
                                            className={`${isMaThematique ? 'mt-draggable-card' : ''}${ssIsObsolete ? ' mt-draggable-card--obsolete' : ''}`}
                                        >
                                            {isMaThematique && !ssIsObsolete && (
                                                <span
                                                    className="mt-drag-handle"
                                                    draggable
                                                    onDragStart={(e) => handleDragStart(e, ss, cat)}
                                                    onDragEnd={handleDragEnd}
                                                    title="Glisser pour déplacer le sujet"
                                                    aria-label="Glisser pour déplacer"
                                                >
                                                    <GripVertical size={14} />
                                                </span>
                                            )}
                                            {isMaThematique && !ssIsObsolete && (
                                                <button
                                                    className="mt-rename-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditSujet({
                                                            id: ss.id,
                                                            custom_ss_id: ss.custom_ss_id,
                                                            label: ss.label,
                                                            nb_questions: ss.nb_questions || 0,
                                                            origin_type: ss.origin_type,
                                                            origin_label: ss.origin_label,
                                                            open_code: ss.open_code,
                                                            open_started_count: ss.open_started_count,
                                                            open_completed_count: ss.open_completed_count,
                                                            documents: ss.documents || [],
                                                            nb_documents: ss.nb_documents || 0,
                                                        });
                                                    }}
                                                    title="Éditer le sujet"
                                                >
                                                    <Pencil size={12} />
                                                </button>
                                            )}
                                            {isMaThematique && ssIsObsolete && (
                                                <span className="mt-obsolete-actions">
                                                    <span className="mt-obsolete-badge"><Archive size={11} /> Obsolète</span>
                                                    <button
                                                        className="mt-restore-btn"
                                                        onClick={(e) => { e.stopPropagation(); handleRestore(ss.custom_ss_id); }}
                                                        disabled={obsoleteBusy === ss.custom_ss_id}
                                                        title="Réanimer ce sujet (repasser en actif)"
                                                    >
                                                        {obsoleteBusy === ss.custom_ss_id ? <Loader2 size={12} className="spin" /> : <RotateCcw size={12} />}
                                                        <span>Réanimer</span>
                                                    </button>
                                                </span>
                                            )}
                                            <QcmCard
                                                sousSujet={labelCourt ? { ...ss, label: labelCourt } : ss}
                                                scope={scope}
                                                hasAccess={has_access}
                                                user={user}
                                                onLocked={onLocked}
                                                isIgnored={ignoredIds.has(ss.id)}
                                                isLastPlayed={ss.id === lastSousSujetId}
                                                onStart={(id, sessionMode) => {
                                                    onStart(id, sessionMode, ss.nb_questions);
                                                }}
                                                onDirectLaunch={onDirectLaunch}
                                            />
                                            {isMaThematique && !ssIsObsolete && ss.suggested_label && ss.suggested_label !== ss.label && (
                                                <SuggestedLabel
                                                    customSsId={ss.custom_ss_id}
                                                    suggestion={ss.suggested_label}
                                                    singleSujet={totalSs === 1}
                                                    onDone={onRefresh}
                                                />
                                            )}
                                        </div>
                                        );
                                    };
                                    // Liste de cartes ; en mobile sans abonnement : gratuits d'abord, verrouillés sur une ligne dépliable.
                                    const renderGrid = (list, key, court = null) => {
                                        const card = (ss) => renderCard(ss, court ? court(ss.label) : null);
                                        const locked = isMobile && !has_access && !isMaThematique && !filterFreeOnly ? list.filter(ss => !ss.is_demo) : [];
                                        if (!locked.length) {
                                            return <div className="sl-qcm-grid">{list.map(card)}</div>;
                                        }
                                        const free = list.filter(ss => ss.is_demo);
                                        const open = !!openLocked[key] || !!searchLc;
                                        const nq = locked.reduce((t, ss) => t + (ss.nb_questions || 0), 0);
                                        return (
                                            <>
                                                {free.length > 0 && <div className="sl-qcm-grid">{free.map(card)}</div>}
                                                <div className={`sl-locked-group${open ? ' is-open' : ''}`}>
                                                    <button type="button" className="sl-locked-group__toggle" aria-expanded={open}
                                                        onClick={() => setOpenLocked(o => ({ ...o, [key]: !o[key] }))}>
                                                        <Lock size={14} />
                                                        <span>{locked.length} sujet{locked.length > 1 ? 's' : ''} avec l’abonnement{nq > 0 ? ` · ${nq.toLocaleString('fr-FR')} questions` : ''}</span>
                                                        <ChevronRight size={14} className={`sl-category-chevron ${open ? 'open' : ''}`} />
                                                    </button>
                                                    {open && <div className="sl-qcm-grid">{locked.map(card)}</div>}
                                                </div>
                                            </>
                                        );
                                    };
                                    const groupes = isMaThematique ? null : groupesParDomaine(filteredSousSujets);
                                    if (!groupes) {
                                        return (
                                            <>
                                                {renderGrid(filteredSousSujets, String(cat.id))}
                                                {!isMaThematique && filteredSousSujets.length > 0 && (
                                                    <RequestMoreQuestions scope={scope} requests={contentRequests}
                                                        category={cat} sujets={cat.sous_sujets} />
                                                )}
                                            </>
                                        );
                                    }
                                    return (
                                        <div className="sl-domaines">
                                            {groupes.map(g => {
                                                const cle = `${cat.id}:${g.id}`;
                                                const ouvert = searchLc || filterFreeOnly ? true : !!openDomaines[cle];
                                                const nNouveaux = g.items.filter(ss => ss.nouveau).length;
                                                const gProg = getCatProgression(g.items);
                                                return (
                                                    <section key={g.id} className={`sl-domaine${ouvert ? ' sl-domaine--open' : ''}`}>
                                                        <button type="button" className="sl-domaine-header" aria-expanded={ouvert}
                                                            onClick={() => setOpenDomaines(o => ({ ...o, [cle]: !o[cle] }))}>
                                                            <span className={`sl-category-chevron ${ouvert ? 'open' : ''}`}><ChevronRight size={13} strokeWidth={2} /></span>
                                                            <span className="sl-domaine-text">
                                                                <span className="sl-domaine-title">{g.label}</span>
                                                                <span className="sl-domaine-meta">
                                                                    {g.items.length} sujet{g.items.length > 1 ? 's' : ''}
                                                                    {gProg && gProg.totalQ > 0 && <> · {gProg.answered}/{gProg.totalQ}</>}
                                                                </span>
                                                            </span>
                                                            {nNouveaux > 0 && <span className="sl-category-new">{nNouveaux} nouveau{nNouveaux > 1 ? 'x' : ''}</span>}
                                                        </button>
                                                        {ouvert && (
                                                            <>
                                                                {renderGrid(g.items, cle, libelleCourt)}
                                                                <RequestMoreQuestions scope={scope} requests={contentRequests}
                                                                    category={cat} groupe={g.label} sujets={g.items} />
                                                            </>
                                                        )}
                                                    </section>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                                {filteredSousSujets.length === 0 && (
                                    isMaThematique
                                        ? (isDragging
                                            ? <p className="sl-empty mt-drop-hint">Glissez un sujet ici</p>
                                            : <p className="sl-empty" style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>Aucun sujet dans cette catégorie</p>)
                                        : <p className="sl-empty mt-drop-hint">Aucun sujet disponible pour le moment.</p>
                                )}
                            </div>
                        )}

                        {/* Drop zone affichée UNIQUEMENT pendant un drag actif (sinon les
                            catégories vides semblaient bloquées en mode "drop", masquant
                            l'effet du collapse/expand). */}
                        {!isOpen && isMaThematique && cat.sous_sujets.length === 0 && isDragging && (
                            <div className="sl-category-content sl-category-content--drop-only">
                                <p className="sl-empty mt-drop-hint">Glissez un sujet ici</p>
                            </div>
                        )}
                    </div>
                    </Fragment>
                );
            })}
            {searchLc && !anySearchMatch && (
                <p className="sl-empty" style={{ textAlign: 'center', padding: '28px 0', color: 'var(--text-muted)' }}>
                    Aucun sujet ne correspond à « {search.trim()} ».
                </p>
            )}
            {/* Fallback : si "Non classé" (ucat_0) n'apparaît pas dans la liste,
                la barre n'aurait pas été injectée dans la map → on la rend ici. */}
            {!hasNonClasse && createCatBar}

            {/* Confirm delete category dialog */}
            <ConfirmDialog
                open={!!confirmDelete}
                title="Supprimer la catégorie"
                message={confirmDelete ? (
                    <>Supprimer la catégorie <strong>{confirmDelete.label}</strong> ? Les sujets qu'elle contient seront déplacés dans « Non classé ».</>
                ) : ''}
                confirmLabel="Supprimer"
                cancelLabel="Annuler"
                variant="danger"
                onConfirm={executeDeleteCat}
                onCancel={() => setConfirmDelete(null)}
            />

            {/* Confirm obsolete sujet dialog */}
            <ConfirmDialog
                open={!!confirmObsolete}
                title="Mettre le sujet en obsolète"
                message={confirmObsolete ? (
                    <>Mettre <strong>{confirmObsolete.label}</strong> en obsolète ? Le sujet sera masqué des listes et déplacé dans « Non classé ». Vous pourrez le réanimer à tout moment.</>
                ) : ''}
                confirmLabel="Mettre en obsolète"
                cancelLabel="Annuler"
                variant="danger"
                onConfirm={() => {
                    const pending = confirmObsolete;
                    setConfirmObsolete(null);
                    if (pending) handleObsolete(pending.customSsId, pending.label);
                }}
                onCancel={() => setConfirmObsolete(null)}
            />

            {/* Unified "Éditer le sujet" modal */}
            {editSujet && (
                <SujetEditModal
                    sousSujet={editSujet}
                    thematiqueId={thematiqueId}
                    isAdmin={isAdmin}
                    onClose={() => setEditSujet(null)}
                    onRefresh={onRefresh}
                    onObsolete={editSujet.custom_ss_id ? (() => {
                        // Defer ConfirmDialog open until after the modal close animation,
                        // sinon il s'affiche dessous l'overlay.
                        const target = { customSsId: editSujet.custom_ss_id, label: editSujet.label };
                        setEditSujet(null);
                        setTimeout(() => setConfirmObsolete(target), 0);
                    }) : null}
                />
            )}

            {copyCodeCat && (
                <CategoryCopyCodeModal
                    categoryId={copyCodeCat.id}
                    categoryLabel={copyCodeCat.label}
                    onClose={() => setCopyCodeCat(null)}
                />
            )}

            {/* Blockers modal : sujet can't be obsoleted because codes or sessions are active */}
            {obsoleteBlockers && (
                <div className="sem-overlay" onClick={() => setObsoleteBlockers(null)}>
                    <div className="sem-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
                        <div className="sem-header">
                            <AlertTriangle size={16} />
                            <span className="sem-title">Impossible d'obsoleter « {obsoleteBlockers.label} »</span>
                            <button className="sem-close" onClick={() => setObsoleteBlockers(null)}><X size={18} /></button>
                        </div>
                        <div className="sem-section" style={{ padding: '14px 18px' }}>
                            <p style={{ fontSize: '0.88em', color: 'var(--text-muted)', marginTop: 0 }}>
                                Ce sujet est encore activement utilisé. Attendez que les éléments suivants soient consommés, terminés ou expirés avant de réessayer.
                            </p>
                            {obsoleteBlockers.blockers.available_codes?.length > 0 && (
                                <div style={{ marginBottom: 12 }}>
                                    <strong style={{ fontSize: '0.88em' }}>Codes quiz encore actifs ({obsoleteBlockers.blockers.available_codes.length}) :</strong>
                                    <ul style={{ marginTop: 6, paddingLeft: 18, fontSize: '0.85em' }}>
                                        {obsoleteBlockers.blockers.available_codes.map((c, i) => (
                                            <li key={i}>
                                                <code>{c.code}</code>
                                                <span style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}> · {c.status}</span>
                                                {c.who ? ` — ${c.who}` : ''}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {obsoleteBlockers.blockers.category_codes?.length > 0 && (
                                <div style={{ marginBottom: 12 }}>
                                    <strong style={{ fontSize: '0.88em' }}>Codes catégorie encore actifs ({obsoleteBlockers.blockers.category_codes.length}) :</strong>
                                    <ul style={{ marginTop: 6, paddingLeft: 18, fontSize: '0.85em' }}>
                                        {obsoleteBlockers.blockers.category_codes.map((c, i) => (
                                            <li key={i}>
                                                <code>{c.code}</code>
                                                <span style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}> · {c.status}</span>
                                                {c.who ? ` — ${c.who}` : ''}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {obsoleteBlockers.blockers.in_progress_sessions?.length > 0 && (
                                <div>
                                    <strong style={{ fontSize: '0.88em' }}>Sessions en cours ({obsoleteBlockers.blockers.in_progress_sessions.length}) :</strong>
                                    <ul style={{ marginTop: 6, paddingLeft: 18, fontSize: '0.85em' }}>
                                        {obsoleteBlockers.blockers.in_progress_sessions.map((s, i) => (
                                            <li key={i}>{s.prenom || s.email}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
